/** @jsxImportSource @emotion/react */

import React, { ComponentProps } from 'react';
import IconCircle from '../../components/UI/IconCircle';
import { colors } from '../../style/theme';

export type Category =
  | 'funding'
  | 'actions'
  | 'reach'
  | 'overview'
  | 'team'
  | 'shopify';

interface ColorConfig {
  main: string;
  background: string;
  header?: string;
  dark?: string;
}

export const categoryColors: Record<Category, ColorConfig> = {
  funding: {
    main: colors.wrenOrange,
    background: colors.wrenOrangeLowOpacity,
    dark: '#BC6F00',
  },
  actions: {
    main: colors.successGreen,
    background: colors.successGreenLowOpacity,
    dark: '#007B34',
  },
  reach: {
    main: colors.purple,
    background: '#FAF2FB',
    dark: '#AC3BBA',
  },
  overview: {
    main: colors.blue,
    background: '#E7F0FB',
  },
  team: {
    main: colors.wrenGreen,
    background: colors.wrenGreenLowOpacity,
    dark: '#02483C',
  },
  shopify: {
    main: colors.wrenGreen,
    background: colors.wrenGreenLowOpacity,
    dark: '#02483C',
  },
};

interface RequiredSvgProps {
  fill: string;
  size?: number;
}

export const Svgs: Record<Category, React.FC<RequiredSvgProps>> = {
  funding: ({ fill, size = 48 }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M47.0305 24.3264C47.0291 37.3532 36.5287 47.8124 23.4589 47.8074C10.4857 47.8024 0.0216787 37.2814 0.0318219 24.2529C0.0417275 11.2375 10.557 0.79344 23.639 0.807387C36.5703 0.819769 47.0331 11.3388 47.0318 24.3277L47.0305 24.3264ZM11.0842 24.4032C16.3698 27.528 20.2408 31.9975 23.7216 36.8988C26.6269 31.4433 31.3335 27.8851 36.1347 24.3759C30.759 21.3349 27.0226 16.7648 23.5868 11.8104C20.111 16.7107 16.4242 21.3041 11.0842 24.403V24.4032Z"
        fill={fill}
      />
    </svg>
  ),
  actions: ({ fill, size = 48 }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16.8914 41.823C9.42832 39.7039 -12.099 30.6482 12.2972 0.552368C25.4894 9.131 30.5728 16.679 31.5139 22.9163C25.2038 25.0817 20.2513 28.7524 20.2513 36.0374C16.8144 28.2054 15.2677 18.2441 14.1109 11.4905C13.5309 19.3799 14.7456 33.4125 16.8914 41.823Z"
        fill={fill}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M24.3722 44.2465C22.4781 39.1804 18.5433 22.7106 47.9438 23.6185C48.8066 49.4911 34.5653 48.5361 28.0978 46.7897C30.7751 41.7751 34.2216 37.2493 38.5828 33.4281C33.2327 36.2783 28.4683 39.8938 24.3722 44.2465Z"
        fill={fill}
      />
    </svg>
  ),
  reach: ({ fill, size = 48 }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 47"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1.92808 15.1108V15.18C1.9256 15.6125 1.76943 16.03 1.48746 16.358V16.3633C0.92094 17.0325 0.249512 18.2304 0.249512 20.1459C0.249512 21.882 0.805539 23.1072 1.47487 23.9128C1.76853 24.2701 1.92876 24.7185 1.92808 25.181C1.92808 26.0713 2.28178 26.9251 2.91137 27.5546C3.54095 28.1841 4.39486 28.5378 5.28523 28.5378H6.12451C6.3471 28.5378 6.56058 28.4493 6.71798 28.292C6.87537 28.1346 6.9638 27.9211 6.9638 27.6986V12.5932C6.9638 12.3706 6.87537 12.1572 6.71798 11.9998C6.56058 11.8424 6.3471 11.754 6.12451 11.754H5.28523C4.39486 11.754 3.54095 12.1077 2.91137 12.7372C2.28178 13.3667 1.92808 14.2205 1.92808 15.1108V15.1108ZM44.3309 16.2174L44.2333 16.1996C44.1374 16.1813 44.0509 16.1301 43.9886 16.0549C43.9264 15.9796 43.8923 15.885 43.8924 15.7874V3.15235C43.8924 1.82643 43.0122 0.634788 41.7008 0.181627C40.4502 -0.249505 39.1473 0.101904 38.3772 1.07641C36.739 3.03237 34.9383 4.84624 32.9942 6.49861C30.5152 8.59658 28.1432 10.0914 25.9652 10.9369C25.8064 10.9974 25.6697 11.1047 25.5732 11.2447C25.4768 11.3846 25.425 11.5505 25.4249 11.7204V28.0133C25.4252 28.3414 25.5216 28.6622 25.7023 28.936C25.883 29.2099 26.14 29.4248 26.4415 29.5542C28.4705 30.4238 30.6631 31.8442 32.946 33.7795C34.9047 35.4469 36.7184 37.2772 38.3678 39.251C38.632 39.5825 38.9693 39.8484 39.3533 40.028C39.7373 40.2076 40.1576 40.296 40.5814 40.2864C40.9539 40.2847 41.3237 40.2231 41.6767 40.1039C43.0227 39.657 43.8924 38.5084 43.8924 37.1814V24.5023C43.8925 24.4033 43.9276 24.3075 43.9915 24.2318C44.0554 24.1562 44.1441 24.1056 44.2417 24.089L44.3309 24.0743C45.3401 23.8005 47.2495 22.6288 47.2495 20.1459C47.2495 17.6629 45.3401 16.4912 44.3309 16.2174ZM22.0709 30.2161V12.5932C22.0709 12.3706 21.9825 12.1572 21.8251 11.9998C21.6677 11.8424 21.4542 11.754 21.2317 11.754H11.1602C10.9376 11.754 10.7242 11.8424 10.5668 11.9998C10.4094 12.1572 10.3209 12.3706 10.3209 12.5932V44.4823C10.3209 45.15 10.5862 45.7904 11.0584 46.2625C11.5306 46.7346 12.171 46.9999 12.8388 46.9999H18.3414C18.8644 47.0042 19.3815 46.8888 19.8531 46.6625C20.3246 46.4361 20.738 46.1048 21.0617 45.6939C21.3935 45.2686 21.6167 44.7688 21.712 44.2378C21.8072 43.7069 21.7716 43.1607 21.6083 42.6466C21.4383 42.1032 21.2275 41.4927 21.0029 40.8434C20.1731 38.4412 19.0789 36.9548 18.7872 33.5718C19.6646 33.5526 20.4996 33.1907 21.1134 32.5635C21.7272 31.9362 22.0709 31.0936 22.0709 30.2161Z"
        fill={fill}
      />
    </svg>
  ),
  overview: ({ fill, size = 48 }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      fill="none"
      viewBox="0 0 24 24"
    >
      <path
        fill={fill}
        d="M20.2471 8.50856V3.08676C20.2471 2.39328 19.6847 1.82588 18.9972 1.82588H17.7472C17.0597 1.82588 16.4972 2.39328 16.4972 3.08676V5.10417L12.3347 1.32153C11.8597 0.892825 11.1347 0.892825 10.6597 1.32153L0.209813 10.816C-0.215183 11.1942 0.0473145 11.9129 0.62231 11.9129H2.74729V20.7391C2.74729 21.4326 3.30979 22 3.99728 22H7.74725C8.43475 22 8.99724 21.4326 8.99724 20.7391V14.4347H13.9972V20.7391C13.9972 21.4326 14.5597 22 15.2472 22H18.9972C19.6847 22 20.2471 21.4326 20.2471 20.7391V11.9129H22.3721C22.9471 11.9129 23.2221 11.1942 22.7846 10.816L20.2471 8.50856ZM8.99724 9.39117C8.99724 8.0042 10.1222 6.86941 11.4972 6.86941C12.8722 6.86941 13.9972 8.0042 13.9972 9.39117H8.99724Z"
      />
    </svg>
  ),
  team: ({ fill, size = 48 }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 16 10"
      fill="none"
    >
      <path
        d="M5.0001 0C4.43177 0 3.88671 0.225769 3.48484 0.627641C3.08297 1.02951 2.8572 1.57457 2.8572 2.1429C2.8572 2.71123 3.08297 3.25629 3.48484 3.65816C3.88671 4.06003 4.43177 4.2858 5.0001 4.2858C5.56843 4.2858 6.11349 4.06003 6.51536 3.65816C6.91723 3.25629 7.143 2.71123 7.143 2.1429C7.143 1.57457 6.91723 1.02951 6.51536 0.627641C6.11349 0.225769 5.56843 0 5.0001 0ZM5.0001 5.7144C2.8572 5.7144 0 6.76014 0 8.21445V10.0002H10.0002V8.21445C10.0002 6.76014 7.143 5.7144 5.0001 5.7144Z"
        fill={fill}
      />
      <path
        d="M9.19917 0.627641C9.60105 0.225769 10.1461 0 10.7144 0C11.2828 0 11.8278 0.225769 12.2297 0.627641C12.6316 1.02951 12.8573 1.57457 12.8573 2.1429C12.8573 2.71123 12.6316 3.25629 12.2297 3.65816C11.8278 4.06003 11.2828 4.2858 10.7144 4.2858C10.1461 4.2858 9.60105 4.06003 9.19917 3.65816C8.7973 3.25629 8.57153 2.71123 8.57153 2.1429C8.57153 1.57457 8.7973 1.02951 9.19917 0.627641Z"
        fill={fill}
        fill-opacity="0.3"
      />
      <path
        d="M10.0155 5.75067C10.2526 5.72853 10.488 5.7144 10.7144 5.7144C12.8573 5.7144 15.7145 6.76014 15.7145 8.21445V10.0002H11.4287V8.21445C11.4287 7.18514 10.8598 6.36854 10.0155 5.75067Z"
        fill={fill}
        fill-opacity="0.3"
      />
    </svg>
  ),
  shopify: ({ size = 48 }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 48 48"
    >
      <path
        fill="#7cb342"
        d="M37 12v-1h-3l-3-2h-2c-1-2-2-4-4-4l-2-1c-5 0-8 6-9 10l-3 1-2 1-3 24 23 4 13-3-5-29zm-9-3-2 1V7l2 2zm-4-2 1 3v1l-4 1c0-3 2-5 3-5zm-1-2v1c-1 0-3 2-4 6l-3 1c1-3 3-8 7-8z"
      />
      <path fill="#558b2f" d="M37 11h-3l-3-2-2 35 13-3-5-29v-1z" />
      <path
        fill="#fff"
        d="m25 19-2 4-3-1c-2 0-2 2-2 2 0 2 5 3 5 8 0 4-2 6-5 6-4 0-6-2-6-2l1-4 4 2 1-2c0-2-4-2-4-7 0-3 2-7 8-7l3 1z"
      />
    </svg>
  ),
};

type IconCirclePropsToInclude = Omit<
  ComponentProps<typeof IconCircle>,
  'children'
>;

type Props = {
  category: Category;
  monochrome?: boolean;
  colorOverrides?: {
    main: string;
    background: string;
  };
  size?: number;
} & Omit<IconCirclePropsToInclude, 'size'>;

export default function CategoryIcon({
  category,
  size,
  colorOverrides,
  ...restOfProps
}: Props) {
  const { main, background } = colorOverrides || categoryColors[category];
  const Icon = Svgs[category];

  return (
    <IconCircle
      bgColor={background}
      size={size}
      {...restOfProps}
      css={{ margin: '0 !important' }}
    >
      {/* Icon takes up more of the circle at smaller sizes */}
      <Icon fill={main} size={size && Math.pow(size, 0.8)} />
    </IconCircle>
  );
}
